import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";

function parseIso(value) {
    if (!value) return undefined;
    const [y, m, d] = value.split("-").map(Number);
    if (!y || !m || !d) return undefined;
    return new Date(y, m - 1, d);
}

function toIso(date) {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function formatDisplay(value) {
    const d = parseIso(value);
    if (!d) return "";
    return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

export default function DatePicker({
    value,
    onChange,
    placeholder = "Pick a date",
    clearable = false,
    disabled = false,
    testId,
    className,
}) {
    const [open, setOpen] = useState(false);
    const selected = parseIso(value);

    const handleSelect = (d) => {
        if (!d) {
            onChange?.("");
            setOpen(false);
            return;
        }
        onChange?.(toIso(d));
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    disabled={disabled}
                    data-testid={testId}
                    className={cn(
                        "w-full justify-start text-left font-normal h-10",
                        !selected && "text-muted-foreground",
                        className,
                    )}
                >
                    <CalendarIcon className="mr-2 h-4 w-4 text-primary/70" />
                    {selected ? (
                        <span className="text-foreground">{formatDisplay(value)}</span>
                    ) : (
                        <span>{placeholder}</span>
                    )}
                    {clearable && value && (
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange?.("");
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.stopPropagation();
                                    onChange?.("");
                                }
                            }}
                            className="ml-auto rounded-full p-0.5 hover:bg-secondary"
                            aria-label="Clear date"
                        >
                            <X className="h-3.5 w-3.5 text-muted-foreground" />
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0">
                <Calendar
                    mode="single"
                    selected={selected}
                    onSelect={handleSelect}
                    initialFocus
                />
            </PopoverContent>
        </Popover>
    );
}
